import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import Input from '../../components/input'
import Select from '../../components/select'
import FileUpload from '../../components/file-upload'
import ProgramFeesBulkUpdate from '../universities/components/ProgramFeesBulkUpdate'
import { DegreeOptions, EducationLanguageOptions } from '../../utils/enums'
import { Actions as ApiCallActions } from '../../redux/apiCall/reducers'
import general from '../../utils/general'
import EventBus from '../../infrastructure/event-bus/event-bus'
import useI18n from '../../i18n/useI18n'

const defaultForm = {
  universityId: '',
  name: '',
  nameEn: '',
  nameRu: '',
  faculty: '',
  facultyEn: '',
  facultyRu: '',
  degree: '',
  language: '',
  tuitionFee: '',
  discountedFee: '',
  urlParameter: '',
  description: '',
  durationYears: '',
  isActive: true
}

const ProgramFormPage = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams()
  const isEditMode = !!id
  const { t, language } = useI18n()

  const queryParams = new URLSearchParams(location.search)
  const initialTab = queryParams.get('tab') === 'bulk-fees' ? 'bulk-fees' : 'single'
  const initialUniversityId = queryParams.get('universityId') || ''

  const [activeTab, setActiveTab] = useState(initialTab)
  const [formData, setFormData] = useState({ ...defaultForm, universityId: initialUniversityId })
  const [selectedUniversityOption, setSelectedUniversityOption] = useState(null)
  const [validationErrors, setValidationErrors] = useState({})
  const [submitError, setSubmitError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [loadingProgram, setLoadingProgram] = useState(isEditMode)
  const [bulkUniversityId, setBulkUniversityId] = useState('')
  const [bulkFile, setBulkFile] = useState(null)
  const [bulkUploadStatus, setBulkUploadStatus] = useState('idle')
  const [templateDownloading, setTemplateDownloading] = useState(false)
  const [bulkUploaderKey, setBulkUploaderKey] = useState(0)

  const isLoading = useMemo(() => loadingProgram, [loadingProgram])

  useEffect(() => {
    if (isEditMode) {
      fetchProgram()
    } else {
      setLoadingProgram(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditMode])

  const fetchProgram = () => {
    setLoadingProgram(true)
    dispatch(
      ApiCallActions.Get({
        url: `programs/${id}`,
        showAlertOnError: true,
        showLoading: true,
        onSuccess: ({ data }) => {
          const program = data?.data || data
          if (program) {
            setFormData(mapProgramToForm(program))
            if (program.university) {
              setSelectedUniversityOption({
                value: program.university.id.toString(),
                label: general.getLocaleValue(program.university, 'name', language)
              })
            }
          }
          setLoadingProgram(false)
        },
        onError: ({ errorMessage }) => {
          general.notificationError(errorMessage || t('PROGRAMS_FORM_FETCH_ERROR'))
          setLoadingProgram(false)
          navigate('/programs')
        }
      })
    )
  }

  const mapUniversityOption = (item) => {
    if (!item) return null
    return {
      value: item.id.toString(),
      label: general.getLocaleValue(item, 'name', language)
    }
  }

  const mapProgramToForm = (program = {}) => ({
    universityId: program.universityId?.toString() || program.university?.id?.toString() || '',
    name: program.name || '',
    nameEn: program.nameEn || '',
    nameRu: program.nameRu || '',
    faculty: program.faculty || '',
    facultyEn: program.facultyEn || '',
    facultyRu: program.facultyRu || '',
    degree: program.degree || '',
    language: program.language || '',
    tuitionFee: program.tuitionFee !== undefined && program.tuitionFee !== null ? program.tuitionFee.toString() : '',
    discountedFee: program.discountedFee !== undefined && program.discountedFee !== null ? program.discountedFee.toString() : '',
    urlParameter: program.urlParameter || '',
    description: program.description || '',
    durationYears: program.durationYears !== undefined && program.durationYears !== null ? program.durationYears.toString() : '',
    isActive: program.isActive !== undefined ? program.isActive : true
  })



  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    setSubmitError('')
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const validateForm = () => {
    const errors = {}

    if (!formData.universityId) errors.universityId = t('PROGRAMS_FORM_VALIDATION_UNIVERSITY_REQUIRED')
    if (!formData.name || !formData.name.trim()) errors.name = t('PROGRAMS_FORM_VALIDATION_NAME_REQUIRED')
    if (!formData.degree) errors.degree = t('PROGRAMS_FORM_VALIDATION_DEGREE_REQUIRED')
    if (!formData.language) errors.language = t('PROGRAMS_FORM_VALIDATION_LANGUAGE_REQUIRED')

    const tuition = parseFloat(formData.tuitionFee)
    if (formData.tuitionFee === '' || Number.isNaN(tuition)) errors.tuitionFee = t('PROGRAMS_FORM_VALIDATION_TUITION_REQUIRED')

    if (formData.discountedFee !== '' && Number.isNaN(parseFloat(formData.discountedFee))) {
      errors.discountedFee = t('PROGRAMS_FORM_VALIDATION_DISCOUNTED_FEE_INVALID')
    }

    if (formData.durationYears !== '' && Number.isNaN(parseFloat(formData.durationYears))) {
      errors.durationYears = t('PROGRAMS_FORM_VALIDATION_DURATION_INVALID')
    }

    setValidationErrors(errors)
    if (Object.keys(errors).length > 0) {
      setSubmitError(t('PROGRAMS_FORM_VALIDATION_GENERIC'))
      return false
    }

    return true
  }

  const buildSubmitData = () => {
    const submitData = {
      universityId: parseInt(formData.universityId, 10),
      name: formData.name.trim(),
      degree: formData.degree,
      language: formData.language,
      tuitionFee: parseFloat(formData.tuitionFee)
    }

    general.addOptionalFields(submitData, formData, {
      string: ['nameEn', 'nameRu', 'faculty', 'facultyEn', 'facultyRu', 'urlParameter', 'description'],
      number: ['discountedFee', 'durationYears'],
      boolean: ['isActive']
    })

    return submitData
  }

  const handleBulkUniversityChange = (e) => {
    const { value } = e.target
    setBulkUniversityId(value)
  }

  const handleBulkFileSelect = (file) => {
    setBulkFile(file)
    setBulkUploadStatus(file ? 'ready' : 'idle')
  }

  const handleTemplateDownload = () => {
    if (templateDownloading) {
      return
    }

    if (!bulkUniversityId) {
      general.notificationWarning(t('PROGRAMS_BULK_UPLOAD_UNIVERSITY_REQUIRED'))
      return
    }

    setTemplateDownloading(true)

    dispatch(
      ApiCallActions.Get({
        url: 'programs/excel/template',
        query: { universityId: bulkUniversityId },
        axiosOptions: {
          responseType: 'blob'
        },
        showAlertOnError: true,
        onSuccess: ({ data }) => {
          const blobSource = data?.data || data

          if (!blobSource) {
            general.notificationError(t('PROGRAMS_BULK_UPLOAD_TEMPLATE_ERROR'))
            return
          }

          const blob =
            blobSource instanceof Blob
              ? blobSource
              : new Blob([blobSource], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
              })

          const downloadUrl = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = downloadUrl
          link.download = 'programlar-sablon.xlsx'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(downloadUrl)

          general.messageSuccess(t('PROGRAMS_BULK_UPLOAD_TEMPLATE_READY'))
        },
        onError: ({ errorMessage }) => {
          general.notificationError(errorMessage || t('PROGRAMS_BULK_UPLOAD_TEMPLATE_ERROR'))
        },
        callback: () => setTemplateDownloading(false)
      })
    )
  }

  const handleBulkUpload = () => {
    if (!bulkUniversityId) {
      general.notificationWarning(t('PROGRAMS_BULK_UPLOAD_UNIVERSITY_REQUIRED'))
      return
    }

    if (!bulkFile) {
      general.notificationWarning(t('PROGRAMS_BULK_UPLOAD_NO_FILE'))
      return
    }

    setBulkUploadStatus('processing')

    const formDataUpload = new FormData()
    formDataUpload.append('file', bulkFile)

    dispatch(
      ApiCallActions.Post({
        url: 'programs/excel/upload',
        query: { universityId: bulkUniversityId },
        showAlertOnError: false,
        axiosOptions: {
          data: formDataUpload
        },
        onSuccess: () => {
          setBulkUploadStatus('idle')
          setBulkFile(null)
          setBulkUploaderKey((prev) => prev + 1)

          general.messageSuccess(t('PROGRAMS_BULK_UPLOAD_UPLOAD_SUCCESS'))
          navigate('/programs')
        },
        onError: ({ errorMessage }) => {
          const message = errorMessage || t('PROGRAMS_BULK_UPLOAD_UPLOAD_ERROR')
          setBulkUploadStatus(bulkFile ? 'ready' : 'idle')
          general.notificationError(message)
        }
      })
    )
  }

  const getBulkStatusMessage = () => {
    switch (bulkUploadStatus) {
      case 'ready':
        return t('PROGRAMS_BULK_UPLOAD_STATUS_READY')
      case 'processing':
        return t('PROGRAMS_BULK_UPLOAD_PROCESSING')
      default:
        return t('PROGRAMS_BULK_UPLOAD_STATUS_IDLE')
    }
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${Math.round(bytes / Math.pow(k, i) * 100) / 100} ${sizes[i]}`
  }

  const renderBulkUploadCard = () => {
    const steps = [
      t('PROGRAMS_BULK_UPLOAD_STEP_DOWNLOAD'),
      t('PROGRAMS_BULK_UPLOAD_STEP_FILL'),
      t('PROGRAMS_BULK_UPLOAD_STEP_UPLOAD')
    ]

    return (
      <div className="card m-t-30">
        <div className="body">
          <div className="row">
            <div className="col-lg-7 col-md-12">
              <h5 className="m-b-10">{t('PROGRAMS_BULK_UPLOAD_TITLE')}</h5>
              <p className="text-muted">{t('PROGRAMS_BULK_UPLOAD_DESCRIPTION')}</p>
              <div className="form-group">
                <label>{t('PROGRAMS_BULK_UPLOAD_UNIVERSITY_LABEL')}</label>
                <Select
                  name="bulkUniversityId"
                  value={bulkUniversityId}
                  onChange={handleBulkUniversityChange}
                  fetchUrl="universities"
                  valueKey="id"
                  labelKey="name"
                  searchParamName="name"
                  mapOption={mapUniversityOption}
                  placeholder={t('PROGRAMS_BULK_UPLOAD_UNIVERSITY_PLACEHOLDER')}
                />
              </div>
              <div className="m-t-15">
                {steps.map((step, index) => (
                  <div key={step} className="m-b-5">
                    <strong style={{ display: 'inline-block', width: '20px' }}>{index + 1}.</strong> {step}
                  </div>
                ))}
              </div>
              <div className="m-t-20">
                <button
                  type="button"
                  className="btn btn-raised btn-round btn-primary m-r-10"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                  onClick={handleTemplateDownload}
                  disabled={templateDownloading || !bulkUniversityId}
                >
                  {templateDownloading ? (
                    <>
                      <i className="zmdi zmdi-refresh zmdi-hc-spin m-r-5"></i>
                      {t('PROGRAMS_BULK_UPLOAD_TEMPLATE_DOWNLOADING')}
                    </>
                  ) : (
                    <>
                      <i className="zmdi zmdi-download m-r-5"></i>
                      {t('PROGRAMS_BULK_UPLOAD_TEMPLATE_BUTTON')}
                    </>
                  )}
                </button>
              </div>
            </div>
            <div className="col-lg-5 col-md-12 m-t-20">
              <div className="card">
                <div className="body">
                  <FileUpload
                    key={bulkUploaderKey}
                    accept=".xlsx,.xls,.csv"
                    label={t('PROGRAMS_BULK_UPLOAD_FILE_LABEL')}
                    showPreview={false}
                    onFileSelect={handleBulkFileSelect}
                    disabled={!bulkUniversityId || bulkUploadStatus === 'processing'}
                  />
                  <p className="text-muted small m-t-10">{getBulkStatusMessage()}</p>
                  {bulkFile && (
                    <div className="alert alert-secondary m-t-15">
                      <div className="d-flex align-items-center">
                        <i className="zmdi zmdi-file-text m-r-10"></i>
                        <div>
                          <strong>{bulkFile.name}</strong>
                          <div className="small text-muted">{formatFileSize(bulkFile.size)}</div>
                        </div>
                      </div>
                    </div>
                  )}
                  <button
                    type="button"
                    className="btn btn-primary btn-block m-t-10"
                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                    onClick={handleBulkUpload}
                    disabled={bulkUploadStatus === 'processing' || !bulkFile || !bulkUniversityId}
                  >
                    {bulkUploadStatus === 'processing' ? (
                      <>
                        <i className="zmdi zmdi-refresh zmdi-hc-spin m-r-5"></i>
                        {t('PROGRAMS_BULK_UPLOAD_PROCESSING')}
                      </>
                    ) : (
                      <>
                        <i className="zmdi zmdi-upload m-r-5"></i>
                        {t('PROGRAMS_BULK_UPLOAD_SUBMIT')}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setSubmitError('')

    if (!validateForm()) {
      return
    }

    const payload = buildSubmitData()
    setIsSubmitting(true)

    if (isEditMode) {
      dispatch(
        ApiCallActions.Put({
          url: `programs/${id}`,
          data: payload,
          showAlertOnError: true,
          showLoading: true,
          onSuccess: ({ data }) => {
            const apiData = data?.data || data
            const mergedEntityData = {
              id,
              ...formData,
              ...payload,
              ...apiData
            }

            EventBus.sc.onEntityUpdated({
              entityType: 'program',
              entityId: id,
              entityData: mergedEntityData
            })

            general.messageSuccess(t('PROGRAMS_FORM_UPDATE_SUCCESS'))
            navigate('/programs')
          },
          onError: ({ errorMessage }) => {
            setSubmitError(errorMessage || t('PROGRAMS_FORM_UPDATE_ERROR'))
          },
          callback: () => setIsSubmitting(false)
        })
      )
    } else {
      dispatch(
        ApiCallActions.Post({
          url: 'programs',
          data: payload,
          showAlertOnError: true,
          showLoading: true,
          onSuccess: ({ data }) => {
            const entityData = data?.data || data

            if (entityData?.id) {
              EventBus.sc.onEntityCreated({
                entityType: 'program',
                entityId: entityData.id,
                entityData
              })
            }

            general.messageSuccess(t('PROGRAMS_FORM_CREATE_SUCCESS'))
            navigate('/programs')
          },
          onError: ({ errorMessage }) => {
            setSubmitError(errorMessage || t('PROGRAMS_FORM_CREATE_ERROR'))
          },
          callback: () => setIsSubmitting(false)
        })
      )
    }
  }

  if (isLoading) {
    return (
      <section className="content">
        <div className="container-fluid">
          <div className="row clearfix">
            <div className="col-12 text-center p-5">
              <p>{t('COMMON_LOADING')}</p>
            </div>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="content">
      <div className="block-header">
        <div className="row">
          <div className="col-lg-7 col-md-6 col-sm-12 text-left">
            <h2>
              {activeTab === 'bulk-fees' ? t('PROGRAM_FEES_BULK_UPDATE_HEADER') : (isEditMode ? t('PROGRAMS_FORM_TITLE_EDIT') : t('PROGRAMS_FORM_TITLE_NEW'))}
              <small>{activeTab === 'bulk-fees' ? t('PROGRAMS_BULK_UPLOAD_DESCRIPTION') : (isEditMode ? (formData.name || t('PROGRAMS_FORM_SUBTITLE_EDIT_LOADING')) : t('PROGRAMS_FORM_SUBTITLE_NEW'))}</small>
            </h2>
          </div>
          <div className="col-lg-5 col-md-6 col-sm-12">
            <button
              className="btn btn-white btn-icon btn-round float-right m-l-10"
              type="button"
              onClick={() => {
                if (initialUniversityId) {
                  navigate(`/universities/${initialUniversityId}`)
                } else {
                  navigate('/programs')
                }
              }}
              title={t('PROGRAMS_FORM_BACK')}
            >
              <i className="zmdi zmdi-arrow-left"></i>
            </button>
            <ul className="breadcrumb float-md-right">
              <li className="breadcrumb-item">
                <Link to="/dashboard">
                  <i className="zmdi zmdi-home" style={{ marginRight: '5px' }}></i> {t('COMMON_HOME')}
                </Link>
              </li>
              {initialUniversityId && (
                <li className="breadcrumb-item">
                  <Link to={`/universities/${initialUniversityId}`}>
                    {location.state?.universityName || t('UNIVERSITIES_BREADCRUMB_DETAIL')}
                  </Link>
                </li>
              )}
              <li className="breadcrumb-item">
                <Link to="/programs">{t('PROGRAMS_LIST_TITLE')}</Link>
              </li>
              <li className="breadcrumb-item active">
                {activeTab === 'bulk-fees' ? t('PROGRAM_FEES_BULK_UPDATE_HEADER') : (isEditMode ? t('PROGRAMS_FORM_BREADCRUMB_EDIT') : t('PROGRAMS_FORM_BREADCRUMB_NEW'))}
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="container-fluid">
        <div className="row clearfix">
          <div className="col-lg-12">
            <div className="card">
              <ul className="nav nav-tabs">
                <li className="nav-item">
                  <a
                    href="#program-single"
                    className={`nav-link ${activeTab === 'single' ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault()
                      setActiveTab('single')
                    }}
                  >
                    <i className="zmdi zmdi-plus-circle m-r-5"></i>
                    {t('PROGRAMS_FORM_TAB_SINGLE')}
                  </a>
                </li>
                <li className="nav-item">
                  <a
                    href="#program-bulk"
                    className={`nav-link ${activeTab === 'bulk' ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault()
                      setActiveTab('bulk')
                    }}
                  >
                    <i className="zmdi zmdi-file-text m-r-5"></i>
                    {t('PROGRAMS_FORM_TAB_BULK')}
                  </a>
                </li>
                {(isEditMode || activeTab === 'bulk-fees') && (
                  <li className="nav-item">
                    <a
                      href="#program-bulk-fees"
                      className={`nav-link ${activeTab === 'bulk-fees' ? 'active' : ''}`}
                      onClick={(e) => {
                        e.preventDefault()
                        setActiveTab('bulk-fees')
                      }}
                    >
                      <i className="zmdi zmdi-money-box m-r-5"></i>
                      {t('PROGRAM_FEES_BULK_UPDATE_TITLE')}
                    </a>
                  </li>
                )}
              </ul>
              <div className="tab-content">
                <div className={`tab-pane body ${activeTab === 'single' ? 'active' : ''}`} id="program-single" style={{ paddingTop: '20px' }}>
                  {submitError && (
                    <div className="alert alert-danger" role="alert">
                      {submitError}
                    </div>
                  )}

                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-md-12">
                        <div className="form-group">
                          <label>{t('PROGRAMS_FORM_FIELD_UNIVERSITY')} <span className="text-danger">*</span></label>
                          <Select
                            name="universityId"
                            value={formData.universityId}
                            onChange={handleChange}
                            fetchUrl="universities"
                            valueKey="id"
                            labelKey="name"
                            searchParamName="name"
                            mapOption={mapUniversityOption}
                            prependOptions={selectedUniversityOption ? [selectedUniversityOption] : []}
                            placeholder={t('PROGRAMS_FORM_FIELD_UNIVERSITY_PLACEHOLDER')}
                          />
                          {validationErrors.universityId && <small className="text-danger">{validationErrors.universityId}</small>}
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-4">
                        <div className="form-group">
                          <label>{t('PROGRAMS_FORM_FIELD_NAME_TR')} <span className="text-danger">*</span></label>
                          <Input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder={t('PROGRAMS_FORM_FIELD_NAME_TR_PLACEHOLDER')}
                            required
                          />
                          {validationErrors.name && <small className="text-danger">{validationErrors.name}</small>}
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label>{t('PROGRAMS_FORM_FIELD_NAME_EN')}</label>
                          <Input
                            type="text"
                            name="nameEn"
                            value={formData.nameEn}
                            onChange={handleChange}
                            placeholder={t('PROGRAMS_FORM_FIELD_NAME_EN_PLACEHOLDER')}
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label>{t('PROGRAMS_FORM_FIELD_NAME_RU')}</label>
                          <Input
                            type="text"
                            name="nameRu"
                            value={formData.nameRu}
                            onChange={handleChange}
                            placeholder={t('PROGRAMS_FORM_FIELD_NAME_RU_PLACEHOLDER')}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-4">
                        <div className="form-group">
                          <label>{t('PROGRAMS_FORM_FIELD_FACULTY_TR')}</label>
                          <Input
                            type="text"
                            name="faculty"
                            value={formData.faculty}
                            onChange={handleChange}
                            placeholder={t('PROGRAMS_FORM_FIELD_FACULTY_TR_PLACEHOLDER')}
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label>{t('PROGRAMS_FORM_FIELD_FACULTY_EN')}</label>
                          <Input
                            type="text"
                            name="facultyEn"
                            value={formData.facultyEn}
                            onChange={handleChange}
                            placeholder={t('PROGRAMS_FORM_FIELD_FACULTY_EN_PLACEHOLDER')}
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label>{t('PROGRAMS_FORM_FIELD_FACULTY_RU')}</label>
                          <Input
                            type="text"
                            name="facultyRu"
                            value={formData.facultyRu}
                            onChange={handleChange}
                            placeholder={t('PROGRAMS_FORM_FIELD_FACULTY_RU_PLACEHOLDER')}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-4">
                        <div className="form-group">
                          <label>{t('PROGRAMS_FORM_FIELD_DEGREE')} <span className="text-danger">*</span></label>
                          <Select
                            name="degree"
                            value={formData.degree}
                            onChange={handleChange}
                            options={[{ value: '', label: t('PROGRAMS_FORM_FIELD_DEGREE_PLACEHOLDER') }, ...DegreeOptions]}
                            placeholder={t('PROGRAMS_FORM_FIELD_DEGREE_PLACEHOLDER')}
                          />
                          {validationErrors.degree && <small className="text-danger">{validationErrors.degree}</small>}
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label>{t('PROGRAMS_FORM_FIELD_LANGUAGE')} <span className="text-danger">*</span></label>
                          <Select
                            name="language"
                            value={formData.language}
                            onChange={handleChange}
                            options={[{ value: '', label: t('PROGRAMS_FORM_FIELD_LANGUAGE_PLACEHOLDER') }, ...EducationLanguageOptions]}
                            placeholder={t('PROGRAMS_FORM_FIELD_LANGUAGE_PLACEHOLDER')}
                          />
                          {validationErrors.language && <small className="text-danger">{validationErrors.language}</small>}
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label>{t('PROGRAMS_FORM_FIELD_DURATION_YEARS')}</label>
                          <Input
                            type="number"
                            min="0"
                            name="durationYears"
                            value={formData.durationYears}
                            onChange={handleChange}
                            placeholder={t('PROGRAMS_FORM_FIELD_DURATION_YEARS_PLACEHOLDER')}
                          />
                          {validationErrors.durationYears && <small className="text-danger">{validationErrors.durationYears}</small>}
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-4">
                        <div className="form-group">
                          <label>{t('PROGRAMS_FORM_FIELD_TUITION_FEE')} <span className="text-danger">*</span></label>
                          <Input
                            type="number"
                            min="0"
                            name="tuitionFee"
                            value={formData.tuitionFee}
                            onChange={handleChange}
                            placeholder={t('PROGRAMS_FORM_FIELD_TUITION_FEE_PLACEHOLDER')}
                          />
                          {validationErrors.tuitionFee && <small className="text-danger">{validationErrors.tuitionFee}</small>}
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label>{t('PROGRAMS_FORM_FIELD_DISCOUNTED_FEE')}</label>
                          <Input
                            type="number"
                            min="0"
                            name="discountedFee"
                            value={formData.discountedFee}
                            onChange={handleChange}
                            placeholder={t('PROGRAMS_FORM_FIELD_DISCOUNTED_FEE_PLACEHOLDER')}
                          />
                          {validationErrors.discountedFee && <small className="text-danger">{validationErrors.discountedFee}</small>}
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label>{t('PROGRAMS_FORM_FIELD_URL_PARAMETER')}</label>
                          <Input
                            type="text"
                            name="urlParameter"
                            value={formData.urlParameter}
                            onChange={handleChange}
                            placeholder={t('PROGRAMS_FORM_FIELD_URL_PARAMETER_PLACEHOLDER')}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-12">
                        <div className="form-group">
                          <label>{t('PROGRAMS_FORM_FIELD_DESCRIPTION')}</label>
                          <textarea
                            className="form-control"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            rows="4"
                            placeholder={t('PROGRAMS_FORM_FIELD_DESCRIPTION_PLACEHOLDER')}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-12">
                        <div className="form-group">
                          <div className="checkbox">
                            <input
                              id="isActive-program-checkbox"
                              type="checkbox"
                              name="isActive"
                              checked={!!formData.isActive}
                              onChange={handleChange}
                            />
                            <label htmlFor="isActive-program-checkbox">{t('PROGRAMS_FORM_FIELD_IS_ACTIVE')}</label>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-12">
                        <button
                          type="button"
                          className="btn btn-secondary m-r-10"
                          onClick={() => {
                            if (initialUniversityId) {
                              navigate(`/universities/${initialUniversityId}`)
                            } else {
                              navigate('/programs')
                            }
                          }}
                          disabled={isSubmitting}
                        >
                          {t('PROGRAMS_FORM_BUTTON_CANCEL')}
                        </button>
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={isSubmitting}
                        >
                          {isSubmitting
                            ? (isEditMode ? t('PROGRAMS_FORM_BUTTON_UPDATING') : t('PROGRAMS_FORM_BUTTON_SAVING'))
                            : (isEditMode ? t('PROGRAMS_FORM_BUTTON_UPDATE') : t('PROGRAMS_FORM_BUTTON_SAVE'))}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
                <div className={`tab-pane body ${activeTab === 'bulk' ? 'active' : ''}`} id="program-bulk" style={{ paddingTop: '20px' }}>
                  {renderBulkUploadCard()}
                </div>
                <div className={`tab-pane body ${activeTab === 'bulk-fees' ? 'active' : ''}`} id="program-bulk-fees" style={{ paddingTop: '20px' }}>
                  {activeTab === 'bulk-fees' && (
                    <ProgramFeesBulkUpdate
                      universityId={formData.universityId}
                      onBack={() => navigate('/universities/' + formData.universityId)}
                      onSuccess={() => console.log('Fee update success')}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default ProgramFormPage

